/**
 * DaysSupplyBreakdownPopup.jsx
 *
 * Generic compact popup showing how any "Days Supply" value is calculated:
 * stock breakdown + month-by-month consumption log.
 *
 * data shape:
 *   title         string
 *   productName   string
 *   stockBreakdown  [{ label, value, isAddition? }]
 *   totalStock    number
 *   days          number
 *   monthLog      buildMonthlyConsumptionLog rows
 */

import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faBox, faClock } from '@fortawesome/free-solid-svg-icons';
import './DaysSupplyBreakdownPopup.css';

const DaysSupplyBreakdownPopup = ({ data, onClose }) => {
  if (!data) return null;

  const { title, productName, stockBreakdown, totalStock, days, monthLog } = data;

  return (
    <div className="popup-overlay" onClick={onClose}>
      <div className="dsp-popup" onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className="dsp-header">
          <div className="dsp-header-text">
            <span className="dsp-title">{title}</span>
            <span className="dsp-days-badge">{days} days</span>
          </div>
          <button className="dsp-close-btn" onClick={onClose} title="Close">
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        {/* Product name */}
        {productName && (
          <div className="dsp-product-name">{productName}</div>
        )}

        {/* Stock breakdown */}
        <div className="dsp-section">
          <div className="dsp-section-title">
            <FontAwesomeIcon icon={faBox} className="dsp-section-icon" />
            Stock breakdown
          </div>
          <table className="dsp-breakdown-table">
            <tbody>
              {stockBreakdown.map((line, i) => (
                <tr key={i} className={line.isAddition ? 'dsp-plan-row' : ''}>
                  <td>{line.label}</td>
                  <td className="dsp-val">
                    {line.isAddition ? '+' : ''}{line.value.toLocaleString()}
                  </td>
                </tr>
              ))}
              <tr className="dsp-total-row">
                <td>Total</td>
                <td className="dsp-val">{totalStock.toLocaleString()}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Month-by-month log */}
        <div className="dsp-section">
          <div className="dsp-section-title">
            <FontAwesomeIcon icon={faClock} className="dsp-section-icon" />
            Consumption log
          </div>
          <div className="dsp-log-wrap">
            <table className="dsp-log-table">
              <thead>
                <tr>
                  <th>Month</th>
                  <th>Period</th>
                  <th>Forecast/mo</th>
                  <th>Consumed</th>
                  <th>Remaining</th>
                </tr>
              </thead>
              <tbody>
                {monthLog.map((row, i) => (
                  <tr key={i} className={row.runsOut ? 'dsp-row-runs-out' : ''}>
                    <td className="dsp-month-cell">{row.month}</td>
                    <td className="dsp-note-cell">{row.note}</td>
                    <td className="dsp-num-cell">{row.forecast.toLocaleString()}</td>
                    <td className="dsp-num-cell dsp-consumed">−{row.consumed.toLocaleString()}</td>
                    <td className="dsp-num-cell dsp-remaining">
                      {row.runsOut
                        ? <span className="dsp-runs-out-label">~{row.daysUntilOut}d left</span>
                        : row.stockAfter.toLocaleString()
                      }
                    </td>
                  </tr>
                ))}
                {monthLog.length === 0 && (
                  <tr>
                    <td colSpan="5" className="dsp-empty">No forecast data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </div>
  );
};

export default DaysSupplyBreakdownPopup;
